import { useLocation, useNavigate } from 'react-router-dom'
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  useTheme,
  useMediaQuery,
  Button,
  Divider,
  Paper,
  Fade,
  Chip
} from '@mui/material'
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  ExpandLess,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  ExpandMore,
  ArrowDropDown,
  AdminPanelSettings,
  SupervisorAccount
} from '@mui/icons-material'
import { useState, useMemo, useCallback, useEffect } from 'react'
import {
  getNavigationByRole,
  NAV_TYPES,
  USER_ROLES,
  isPathInSection,
  shouldShowCollapseIcon
} from './navigation'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import { logout, selectCurrentUser } from '@/store/slices/authSlice'

// Role configuration for easy management
const ROLE_CONFIG = {
  [USER_ROLES.ADMIN]: {
    label: 'Administrator',
    color: 'error',
    icon: AdminPanelSettings,
    priority: 1
  },
  [USER_ROLES.USER]: {
    label: 'User',
    color: 'primary',
    icon: SupervisorAccount,
    priority: 2
  },
  [USER_ROLES.BRANCH_ADMIN]: {
    label: 'Branch Admin',
    color: 'warning',
    icon: SupervisorAccount,
    priority: 3
  }
  // Add more roles here as needed
  // [USER_ROLES.MANAGER]: {
  //   label: 'Manager',
  //   color: 'info',
  //   icon: BusinessIcon,
  //   priority: 4
  // }
}

// Helper function to get role configuration
const getRoleConfig = (role) => {
  return ROLE_CONFIG[role] || {
    label: role || 'Unknown',
    color: 'default',
    icon: PersonIcon,
    priority: 999
  }
}

// Helper function to check if user has permission for a route
const hasRoutePermission = (userRole, requiredRoles = []) => {
  if (!requiredRoles.length) return true
  return requiredRoles.includes(userRole)
}

const TopNavbar = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const user = useAppSelector(selectCurrentUser)
  
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // State management
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [userMenuAnchor, setUserMenuAnchor] = useState(null)
  const [activeParentId, setActiveParentId] = useState(null)

  // Memoize current path for performance
  const currentPath = useMemo(() => location.pathname, [location.pathname])

  // Get user role configuration
  const currentUserRole = user?.role || USER_ROLES.USER
  const roleConfig = useMemo(() => getRoleConfig(currentUserRole), [currentUserRole])

  // Get navigation items based on user role with permission filtering
  const navigationItems = useMemo(() => {
    const items = getNavigationByRole(currentUserRole)
    
    // Filter items based on role permissions if needed
    const filteredItems = items.filter(item => {
      // If item has role restrictions, check permissions
      if (item.allowedRoles && item.allowedRoles.length > 0) {
        return hasRoutePermission(currentUserRole, item.allowedRoles)
      }
      return true
    })

    console.log('Navigation items for role:', currentUserRole, filteredItems)
    return filteredItems
  }, [currentUserRole])

  // Initialize collapse states based on current path (for mobile menu)
  const [collapseStates, setCollapseStates] = useState(() => {
    const initialStates = {}
    navigationItems.forEach(item => {
      if (item.type === NAV_TYPES.PARENT) {
        initialStates[item.id] = isPathInSection(currentPath, item.path)
      }
    })
    return initialStates
  })

  // Set active parent based on current path
  useEffect(() => {
    const activeParent = navigationItems.find(item =>
      item.type === NAV_TYPES.PARENT && isPathInSection(currentPath, item.path)
    )
    setActiveParentId(activeParent?.id || null)
  }, [currentPath, navigationItems])

  // Update collapse states when path changes
  useEffect(() => {
    setCollapseStates(prev => {
      const newStates = { ...prev }
      navigationItems.forEach(item => {
        if (item.type === NAV_TYPES.PARENT) {
          newStates[item.id] = isPathInSection(currentPath, item.path)
        }
      })
      return newStates
    })
  }, [currentPath, navigationItems])

  // Handlers
  const handleNavigation = useCallback((path) => {
    console.log('Navigating to:', path)
    navigate(path)
    setMobileMenuOpen(false)
  }, [navigate])

  const handleCollapseToggle = useCallback((itemId) => {
    setCollapseStates(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }))
  }, [])

  const handleMobileMenuToggle = useCallback(() => {
    setMobileMenuOpen(prev => !prev)
  }, [])

  const handleUserMenuOpen = useCallback((event) => {
    setUserMenuAnchor(event.currentTarget)
  }, [])

  const handleUserMenuClose = useCallback(() => {
    setUserMenuAnchor(null)
  }, [])

  const handleParentTabClick = useCallback((itemId) => {
    setActiveParentId(prev => prev === itemId ? null : itemId)
  }, [])

  const isActive = useCallback((path) => currentPath === path, [currentPath])

  // Memoized user info with role configuration
  const userInfo = useMemo(() => ({
    initial: user?.userName?.charAt(0) || user?.name?.charAt(0) || 'U',
    name: user?.userName || user?.name || 'Unknown User',
    role: currentUserRole,
    roleConfig: roleConfig
  }), [user, currentUserRole, roleConfig])

  // Get active parent item
  const activeParentItem = useMemo(() => {
    return navigationItems.find(item => item.id === activeParentId)
  }, [navigationItems, activeParentId])

  // Desktop navigation item component
  const DesktopNavigationItem = useCallback(({ item }) => {
    const Icon = item.icon
    const hasSubmenu = item.type === NAV_TYPES.PARENT && item.children && item.children.length > 0
    const isItemActive = hasSubmenu
      ? isPathInSection(currentPath, item.path) || activeParentId === item.id
      : isActive(item.path)

    // Check if user has permission for this item
    if (item.allowedRoles && !hasRoutePermission(currentUserRole, item.allowedRoles)) {
      return null
    }

    if (hasSubmenu) {
      return (
        <Button
          key={item.id}
          startIcon={<Icon />}
          endIcon={<ArrowDropDown />}
          onClick={() => handleParentTabClick(item.id)}
          sx={{
            mx: 1,
            color: isItemActive ? 'primary.main' : 'inherit',
            fontWeight: isItemActive ? 600 : 400,
            textTransform: 'none'
          }}
        >
          {item.name}
        </Button>
      )
    }

    return (
      <Button
        key={item.id}
        startIcon={<Icon />}
        onClick={() => handleNavigation(item.path)}
        sx={{
          mx: 1,
          color: isItemActive ? 'primary.main' : 'inherit',
          fontWeight: isItemActive ? 600 : 400,
          textTransform: 'none'
        }}
      >
        {item.name}
      </Button>
    )
  }, [currentPath, handleNavigation, handleParentTabClick, isActive, activeParentId, currentUserRole])

  // Mobile navigation item component
  const MobileNavigationItem = useCallback(({ item }) => {
    const Icon = item.icon
    const hasSubmenu = item.type === NAV_TYPES.PARENT
    const isExpanded = collapseStates[item.id]
    const isItemActive = hasSubmenu
      ? isPathInSection(currentPath, item.path)
      : isActive(item.path)

    // Check if user has permission for this item
    if (item.allowedRoles && !hasRoutePermission(currentUserRole, item.allowedRoles)) {
      return null
    }

    return (
      <>
        <ListItem disablePadding>
          <ListItemButton
            onClick={hasSubmenu
              ? () => handleCollapseToggle(item.id)
              : () => handleNavigation(item.path)
            }
            selected={!hasSubmenu && isActive(item.path)}
            sx={{ py: 1.5 }}
          >
            <ListItemIcon>
              <Icon color={isItemActive ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary={item.name} />
            {shouldShowCollapseIcon(item) && (
              isExpanded ? <ExpandLess /> : <ExpandMore />
            )}
          </ListItemButton>
        </ListItem>

        {/* Render children if exists */}
        {item.children && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map(child => (
                <MobileSubmenuItem key={child.id} child={child} />
              ))}
            </List>
          </Collapse>
        )}
      </>
    )
  }, [
    collapseStates,
    currentPath,
    handleNavigation,
    handleCollapseToggle,
    isActive,
    currentUserRole
  ])

  // Mobile submenu item component
  const MobileSubmenuItem = useCallback(({ child }) => {
    const ChildIcon = child.icon

    // Check if user has permission for this child item
    if (child.allowedRoles && !hasRoutePermission(currentUserRole, child.allowedRoles)) {
      return null
    }

    return (
      <ListItemButton
        sx={{ pl: 4 }}
        onClick={() => handleNavigation(child.path)}
        selected={isActive(child.path)}
      >
        {ChildIcon && (
          <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
            <ChildIcon fontSize="small" />
          </ListItemIcon>
        )}
        <ListItemText primary={child.name} />
      </ListItemButton>
    )
  }, [handleNavigation, isActive, currentUserRole])

  const handleNavigate = (path) => {
    navigate(path)
    handleUserMenuClose()
  }

  const handleLogout = () => {
    handleUserMenuClose()
    dispatch(logout())
  }

  return (
    <>
      <AppBar position="fixed" elevation={1} sx={{ bgcolor: 'background.paper', color: 'text.primary', zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          {/* Left section - Logo and Desktop Navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            {/* Logo */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                mr: 4
              }}
              onClick={() => handleNavigation('/')}
            >
              <Box
                component="img"
                src="/ssc_logo.png"
                alt="SSC Logo"
                sx={{
                  width: 40,
                  height: 40,
                  mr: 1.5,
                  objectFit: 'contain'
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: 'primary.main'
                }}
              >
                SSC Ticket
              </Typography>
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {navigationItems.map(item => (
                  <DesktopNavigationItem key={item.id} item={item} />
                ))}
              </Box>
            )}
          </Box>

          {/* Right section - User Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Desktop User Info */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {userInfo.name}
                  </Typography>
                  <Chip
                    icon={<userInfo.roleConfig.icon />}
                    label={userInfo.roleConfig.label}
                    size="small"
                    color={userInfo.roleConfig.color}
                    variant="outlined"
                    sx={{
                      height: 20,
                      fontSize: '0.75rem',
                      '& .MuiChip-icon': {
                        fontSize: '0.875rem'
                      }
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* User Avatar/Menu */}
            <IconButton
              onClick={handleUserMenuOpen}
              sx={{ p: 0.5 }}
              aria-label="User menu"
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: `${userInfo.roleConfig.color}.main`,
                  fontSize: '0.875rem'
                }}
              >
                {userInfo.initial}
              </Avatar>
            </IconButton>

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                onClick={handleMobileMenuToggle}
                sx={{ ml: 1 }}
                aria-label="Toggle mobile menu"
              >
                {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
            )}
          </Box>
        </Toolbar>

        {/* Submenu Bar - Shows below main navigation when parent is active */}
        <Collapse
          in={!isMobile && Boolean(activeParentId && activeParentItem?.children)}
          timeout={{
            enter: 300,
            exit: 200
          }}
          unmountOnExit
        >
          <Paper
            elevation={2}
            sx={{
              bgcolor: 'background.default',
              borderTop: 1,
              borderColor: 'divider',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Fade
              in={Boolean(activeParentId && activeParentItem?.children)}
              timeout={{
                enter: 400,
                exit: 150
              }}
            >
              <Box sx={{ px: 3, py: 1.5 }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  transition: 'all 0.2s ease-in-out'
                }}>
                  {activeParentItem?.children?.map(child => {
                    // Check permissions for child items
                    if (child.allowedRoles && !hasRoutePermission(currentUserRole, child.allowedRoles)) {
                      return null
                    }

                    const ChildIcon = child.icon
                    return (
                      <Button
                        key={child.id}
                        startIcon={ChildIcon && <ChildIcon />}
                        onClick={() => handleNavigation(child.path)}
                        size="small"
                        sx={{
                          fontWeight: isActive(child.path) ? 600 : 400,
                          textTransform: 'none',
                          bgcolor: isActive(child.path) ? 'primary.main' : 'transparent',
                          color: isActive(child.path) ? 'primary.contrastText' : 'text.primary',
                          borderRadius: 2,
                          px: 2,
                          py: 0.5,
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            bgcolor: isActive(child.path) ? 'primary.dark' : 'action.hover',
                            transform: 'translateY(-1px)',
                            boxShadow: isActive(child.path) ? 2 : 1
                          }
                        }}
                      >
                        {child.name}
                      </Button>
                    )
                  })}
                </Box>
              </Box>
            </Fade>
          </Paper>
        </Collapse>
      </AppBar>

      {/* Spacer to push content below fixed AppBar */}
      <Toolbar />
      {/* Dynamic spacer for submenu when active */}
      <Collapse
        in={!isMobile && Boolean(activeParentId && activeParentItem?.children)}
        timeout={{
          enter: 300,
          exit: 200
        }}
      >
        <Box sx={{ height: 52 }} />
      </Collapse>

      {/* User Menu Dropdown */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {/* Mobile: Show user info in menu */}
        {isMobile && (
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {userInfo.name}
              </Typography>
            </Box>
            <Chip
              icon={<userInfo.roleConfig.icon />}
              label={userInfo.roleConfig.label}
              size="small"
              color={userInfo.roleConfig.color}
              variant="filled"
            />
          </Box>
        )}

        {/* Profile Menu Item */}
        <MenuItem onClick={() => handleNavigate('/profile')}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>

        {/* Settings Menu Item - Only show for admin roles */}
        {(currentUserRole === USER_ROLES.ADMIN || currentUserRole === USER_ROLES.BRANCH_ADMIN) && (
          <MenuItem onClick={() => handleNavigate('/settings')}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            Settings
          </MenuItem>
        )}

        <Divider />

        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          ອອກຈາກລະບົບ
        </MenuItem>
      </Menu>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="top"
        open={isMobile && mobileMenuOpen}
        onClose={handleMobileMenuToggle}
        sx={{
          '& .MuiDrawer-paper': {
            top: 64, // Height of AppBar
            maxHeight: 'calc(100vh - 64px)',
            zIndex: theme.zIndex.appBar - 1,
          },
        }}
      >
        <List sx={{ pt: 1 }}>
          {navigationItems.map(item => (
            <MobileNavigationItem key={item.id} item={item} />
          ))}
        </List>
      </Drawer>
    </>
  )
}

export default TopNavbar